<template>
  <div class="relative tablet-h:max-w-[380px]">
    <div
      v-if="props.plan.headerText"
      class="relative mb-[-62px] h-[82px] w-full rounded-t-[20px] bg-[#DFF2F4] text-center text-[15px] leading-[30px] font-medium text-green-900 font-primary tablet-h:absolute tablet-h:top-[-20px]"
    >
      {{ props.plan.headerText }}

      <div
        class="absolute bottom-0 left-0 h-[30px] w-full bg-gradient-to-b from-[#DFF2F4] to-[#F7F7F9]"
      />
    </div>

    <div
      class="flex h-full flex-col overflow-hidden rounded-[30px] border-10 border-gray-200/20"
    >
      <div
        class="relative z-6 flex grow flex-col rounded-[20px] border border-black/8 bg-white px-[26px] pt-10 pb-[26px] tablet-v:pr-[30px] tablet-v:pl-9"
        :class="classes"
      >
        <div class="mb-6 text-[24px] leading-[38px] text-gray-800 font-primary">
          {{ props.plan.name }}
        </div>

        <div
          v-if="isFree"
          class="mb-[18px] text-[38px] leading-[38px] font-semibold text-gray-800 font-primary tablet-h:text-[34px]"
        >
          Free
        </div>

        <div v-else-if="isPAYG" class="mb-[16px] flex flex-col">
          <div class="flex items-baseline">
            <div
              class="mr-1 text-[38px] leading-[38px] font-semibold text-gray-800 font-primary tablet-h:text-[34px]"
            >
              {{ paygPrices.copyright?.details?.renderedPrice }}
            </div>
            <div
              class="text-[13px] leading-[25px] font-secondary text-gray-800"
            >
              {{ paygPrices.copyright?.details?.suffix }}
            </div>
          </div>
          <div class="flex items-baseline">
            <div
              class="mr-1 flex items-center text-[20px] leading-[38px] font-semibold text-gray-800 font-primary"
            >
              <span v-if="!isFree">+</span>
              {{ paygPrices.subscription?.details?.renderedPrice }}
            </div>
            <div
              class="text-[13px] leading-[16px] font-secondary text-gray-800"
            >
              {{ paygPrices.subscription?.details?.suffix }}
            </div>
          </div>
        </div>

        <div v-else class="mb-[18px]">
          <div class="flex items-baseline">
            <div
              class="mr-1 text-[38px] leading-[38px] font-semibold text-gray-800 font-primary"
            >
              {{ props.plan.items[0].prices[0].details?.renderedPrice }}
            </div>
            <div
              class="text-[13px] leading-[25px] font-secondary text-gray-800"
            >
              {{ props.plan.items[0].prices[0].details?.suffix }}
            </div>
          </div>

          <Pill v-if="plan.priceCallout" class="mt-2.5 slant" success>
            {{ plan.priceCallout }}
          </Pill>
        </div>

        <div v-if="isFree" class="hidden min-h-[37px] tablet-h:block" />

        <div
          class="text-[14px] leading-[22px] text-gray-800 font-secondary tablet-h:max-h-[48px] tablet-h:min-h-[48px]"
        >
          {{ props.plan.description }}
        </div>

        <div class="mt-6 mb-5 h-px w-full bg-gray-200" />

        <div
          class="mb-[18px] text-[22px] leading-[24px] font-medium text-gray-800 font-secondary"
        >
          Includes
        </div>

        <div class="mb-auto flex flex-col gap-y-[14px]">
          <div
            v-for="benefit in props.plan.benefits"
            :key="`${props.plan.name}-${benefit}`"
            class="flex"
          >
            <Icon name="FilledCircleCheck" :size="16" class="mr-[13px]" />
            <div
              class="-mt-0.5 text-[14px] leading-[20px] text-gray-800 font-secondary"
            >
              <component :is="render(benefit)" />
            </div>
          </div>
        </div>

        <UiButton
          class="w-full min-h-[46px]! mt-[30px]"
          :primary="!isFree"
          :loading="props.disabled"
          large
          @click="handleClick"
        >
          {{ buttonCopy }}
        </UiButton>

        <div
          v-if="props.plan.headerText"
          class="pointer-events-none absolute bottom-0 left-0 z-[-1] h-[353px] w-full rounded-[19px] bg-cover bg-center bg-no-repeat select-none"
          :style="{ backgroundImage: `url(${GradientImage})` }"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, h } from 'vue'
import { Icon, Pill, UiButton } from '@dacr/ui'
import {
  PlanItemStatus,
  PlanPriceType,
  type Plan,
} from '@/shared/graphql/legacy/generated'
import { SelectPlanType } from '@/shared/types/index'
import { useBillingStore } from '@/features/billing/store'
import GradientImage from '@/assets/images/Plan-Gradient.png'

const props = defineProps<{
  plan: Plan
  type: SelectPlanType
  disabled?: boolean
}>()

const emit = defineEmits(['selected'])

const billingStore = useBillingStore()

const isPAYG = computed(() => props.plan.items.length === 1)
const isFree = computed(
  () => isPAYG.value && !props.plan.items[0].prices[0].details?.amount
)

const classes = computed(() => ({
  'border-green-500': props.plan.headerText,
}))

const paygPrices = computed(() => {
  const subscription = props.plan.items[0].prices.find(
    (price) => price.type === PlanPriceType.Subscription
  )
  const copyright = props.plan.items[0].prices.find(
    (price) => price.type === PlanPriceType.File
  )
  return { subscription, copyright }
})

const handleClick = () => {
  emit('selected', props.plan, lowestActivePlanItem.value)
}

const isSubscribedToPlan = computed(() => {
  return props.plan.items.some(
    (item) => billingStore.billing?.plan.id === item.productId
  )
})

const isSubscribedToBundlePlan = computed(
  () => isSubscribedToPlan.value && props.plan.items.length > 1
)

const lowestActivePlanItem = computed(() =>
  props.plan.items.find(
    (item, index) =>
      item.status === PlanItemStatus.Active ||
      index === props.plan.items.length - 1
  )
)

const buttonCopy = computed(() => {
  if (props.type === SelectPlanType.Change && isSubscribedToBundlePlan.value) {
    return 'Upgrade limit'
  }
  if (props.type === SelectPlanType.Change && isSubscribedToPlan.value) {
    return 'See account details'
  }
  if (props.type === SelectPlanType.Change && !isSubscribedToPlan.value) {
    return 'Switch plan'
  }
  return 'Get started'
})

const render = (benefit: string) => {
  if (benefit.includes('Dacr AI')) {
    return h('div', [
      h(
        'span',
        {
          class:
            'bg-linear-to-r from-yellow-500 via-[#F84600] to-[#CE489A] bg-clip-text text-transparent font-medium mr-1',
        },
        'Dacr AI'
      ),
      benefit.replace('Dacr AI ', ''),
    ])
  }
  return h('span', benefit)
}
</script>
